import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { gradeAtLeast, useAuth } from "../lib/auth.jsx";
import {
  Sun,
  Lock,
  ArrowUpRight,
  Mail,
  Video,
  Users,
  Sparkles,
  Loader2,
  RotateCcw,
} from "lucide-react";

const NLP_MAGICK_TRAINING_URL =
  "https://www.nlpmagick.net/nlp-magick-for-nlp-master-practitioners/";
const CONTACT_EMAIL = "joost@tiouw.com";

export default function TarotCelticCrossPage() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();

  const isAdmin = user?.role === "admin";
  const gradeOk = gradeAtLeast(user, "Neophyte");
  const courseOk = isAdmin || !!user?.tarot_course_completed;
  const unlocked = gradeOk && courseOk;

  return (
    <main
      data-testid="tarot-celtic-page"
      className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24"
    >
      <p className="label-small text-[#8B3A3A] mb-4 flex items-center gap-2">
        <Sun size={12} /> {t("tarot.celtic_eyebrow")}
      </p>
      <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918] mb-10">
        {t("tarot.celtic_heading")}
      </h1>

      {unlocked ? (
        <CelticCrossWorking t={t} onCountBump={refresh} />
      ) : !gradeOk ? (
        <ProbationerGate t={t} />
      ) : (
        <CourseOffer t={t} />
      )}
    </main>
  );
}

/* --------------- Gate views (unchanged) --------------- */
function ProbationerGate({ t }) {
  return (
    <section data-testid="tarot-celtic-initiate-gate" className="space-y-8">
      <div className="border border-[#8B3A3A]/60 bg-[#EAE0CA]/50 p-8 md:p-10">
        <Lock size={20} className="text-[#8B3A3A] mb-4" />
        <p className="label-small text-[#8B3A3A] mb-3">{t("tarot.celtic_initiate_label")}</p>
        <p className="font-display italic text-2xl md:text-3xl text-[#1A1918] leading-snug mb-4">
          {t("tarot.celtic_initiate_title")}
        </p>
        <p className="text-[#4A4238] leading-relaxed max-w-2xl mb-6">
          {t("tarot.celtic_initiate_body")}
        </p>
        <a
          href={NLP_MAGICK_TRAINING_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="celtic-initiate-cta"
          className="btn-primary inline-flex items-center gap-2"
        >
          {t("tarot.celtic_initiate_cta")} <ArrowUpRight size={12} />
        </a>
      </div>
    </section>
  );
}

function CourseOffer({ t }) {
  const subject = encodeURIComponent(t("tarot.celtic_course_mail_subject"));
  const mailto = `mailto:${CONTACT_EMAIL}?subject=${subject}`;
  return (
    <section data-testid="tarot-celtic-course-offer" className="space-y-8">
      <div className="border border-[#8B3A3A]/60 bg-[#EAE0CA]/50 p-8 md:p-10">
        <Lock size={20} className="text-[#8B3A3A] mb-4" />
        <p className="label-small text-[#8B3A3A] mb-3">{t("tarot.celtic_course_label")}</p>
        <p className="font-display italic text-2xl md:text-3xl text-[#1A1918] leading-snug mb-4">
          {t("tarot.celtic_course_title")}
        </p>
        <p className="text-[#4A4238] leading-relaxed max-w-2xl">{t("tarot.celtic_course_body")}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#C1B49A]">
        <CourseOption
          testId="celtic-course-online"
          icon={<Video size={18} className="text-[#8B3A3A]" />}
          eyebrow={t("tarot.celtic_course_online_eyebrow")}
          title={t("tarot.celtic_course_online_title")}
          price={t("tarot.celtic_course_online_price")}
          body={t("tarot.celtic_course_online_body")}
          highlight
        />
        <CourseOption
          testId="celtic-course-live"
          icon={<Users size={18} className="text-[#8B3A3A]" />}
          eyebrow={t("tarot.celtic_course_live_eyebrow")}
          title={t("tarot.celtic_course_live_title")}
          price={t("tarot.celtic_course_live_price")}
          body={t("tarot.celtic_course_live_body")}
        />
      </div>
      <div className="border border-[#C1B49A] bg-[#F4EEDF] p-6 md:p-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <div className="flex-1">
          <p className="label-small text-[#8B3A3A] mb-1">{t("tarot.celtic_course_mail_eyebrow")}</p>
          <p className="text-[#1A1918] font-medium leading-snug">{t("tarot.celtic_course_mail_body")}</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#7A7061] mt-1">{CONTACT_EMAIL}</p>
        </div>
        <a
          href={mailto}
          data-testid="celtic-course-mailto"
          className="btn-primary inline-flex items-center gap-2 shrink-0"
        >
          <Mail size={12} /> {t("tarot.celtic_course_mail_cta")}
        </a>
      </div>
    </section>
  );
}

function CourseOption({ testId, icon, eyebrow, title, price, body, highlight }) {
  return (
    <article
      data-testid={testId}
      className={`p-6 md:p-8 ${
        highlight
          ? "bg-[#F4EEDF] md:border-r border-[#C1B49A]"
          : "bg-[#EAE0CA]/40 border-t md:border-t-0 border-[#C1B49A]"
      }`}
    >
      <div className="mb-3">{icon}</div>
      <p className="label-small text-[#8B3A3A] mb-2">{eyebrow}</p>
      <h3 className="font-display text-2xl text-[#1A1918] leading-tight mb-2">{title}</h3>
      <p className="font-mono text-xs tracking-[0.22em] uppercase text-[#8B3A3A] font-semibold mb-3">
        {price}
      </p>
      <p className="text-sm text-[#4A4238] leading-relaxed">{body}</p>
    </article>
  );
}

/* --------------- The actual Celtic Cross working --------------- */
const POSITION_KEYS = Array.from({ length: 11 }, (_, i) => `celtic_pos_${i + 1}`);

function CelticCrossWorking({ t, onCountBump }) {
  const [cards, setCards] = useState([]); // 22 major arcana metadata
  const [loadingCards, setLoadingCards] = useState(true);
  const [question, setQuestion] = useState("");
  // picks[i] = slug at position (i+1), or ""
  const [picks, setPicks] = useState(() => Array(11).fill(""));
  const [reading, setReading] = useState(null); // {id, status, report_markdown, error, readings_count}
  const [polling, setPolling] = useState(false);
  const pollTimerRef = useRef(null);
  const reportRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tarot/major-arcana");
        setCards(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaarten laden mislukt");
      } finally {
        setLoadingCards(false);
      }
    })();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const usedSlugs = useMemo(() => new Set(picks.filter(Boolean)), [picks]);
  const allFilled = picks.every(Boolean) && new Set(picks).size === 11;

  const setPick = (idx, slug) => {
    setPicks((cur) => {
      const next = [...cur];
      next[idx] = slug;
      return next;
    });
  };

  const positionLabels = [
    t("celtic.pos_1"),
    t("celtic.pos_2"),
    t("celtic.pos_3"),
    t("celtic.pos_4"),
    t("celtic.pos_5"),
    t("celtic.pos_6"),
    t("celtic.pos_7"),
    t("celtic.pos_8"),
    t("celtic.pos_9"),
    t("celtic.pos_10"),
    t("celtic.pos_11"),
  ];

  const reset = () => {
    if (polling) return;
    setPicks(Array(11).fill(""));
    setQuestion("");
    setReading(null);
  };

  const pollReading = async (readingId) => {
    try {
      const { data } = await api.get(`/tarot/celtic-cross/reading/${readingId}`);
      setReading(data);
      if (data.status === "done") {
        setPolling(false);
        onCountBump?.();
        // Scroll to report
        setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        return;
      }
      if (data.status === "error") {
        setPolling(false);
        toast.error(data.error || "AI-duiding mislukt");
        return;
      }
      pollTimerRef.current = setTimeout(() => pollReading(readingId), 3000);
    } catch (e) {
      setPolling(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI-duiding niet opgehaald");
    }
  };

  const requestReading = async () => {
    if (!allFilled || polling) return;
    setPolling(true);
    setReading(null);
    try {
      const body = {
        question: question.trim() || null,
        positions: picks.map((slug, i) => ({ position: i + 1, card_slug: slug })),
      };
      const { data } = await api.post("/tarot/celtic-cross/reading", body);
      setReading(data);
      onCountBump?.();
      pollTimerRef.current = setTimeout(() => pollReading(data.id), 3000);
    } catch (e) {
      setPolling(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI-duiding kon niet starten");
    }
  };

  return (
    <section data-testid="tarot-celtic-working" className="space-y-10">
      {/* ---- Instructions ---- */}
      <div className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-6 md:p-8">
        <p className="label-small text-[#8B3A3A] mb-3 flex items-center gap-2">
          <Sparkles size={12} /> {t("celtic.instructions_eyebrow")}
        </p>
        <h2 className="font-display text-2xl md:text-3xl text-[#1A1918] mb-4">
          {t("celtic.instructions_title")}
        </h2>
        <ol className="list-decimal pl-5 space-y-2 text-[15px] text-[#1A1918] leading-relaxed">
          <li>{t("celtic.instr_1")}</li>
          <li>{t("celtic.instr_2")}</li>
          <li>{t("celtic.instr_3")}</li>
          <li>{t("celtic.instr_4")}</li>
        </ol>
      </div>

      {/* ---- Optional question ---- */}
      <div>
        <label htmlFor="celtic-question" className="label-small text-[#8B3A3A] mb-2 block">
          {t("celtic.question_label")}
        </label>
        <textarea
          id="celtic-question"
          data-testid="celtic-question-input"
          rows={2}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          disabled={polling}
          placeholder={t("celtic.question_placeholder")}
          className="input-etched w-full resize-y disabled:opacity-60"
          maxLength={1000}
        />
      </div>

      {/* ---- 11 position pickers ---- */}
      <div className="border border-[#C1B49A]" data-testid="celtic-position-grid">
        {POSITION_KEYS.map((_key, i) => (
          <PositionPicker
            key={i}
            index={i}
            label={positionLabels[i]}
            positionNumber={i + 1}
            value={picks[i]}
            allCards={cards}
            usedSlugs={usedSlugs}
            loading={loadingCards}
            disabled={polling}
            onChange={(slug) => setPick(i, slug)}
          />
        ))}
      </div>

      {/* ---- Actions ---- */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 border-t border-[#C1B49A] pt-6">
        <button
          type="button"
          data-testid="celtic-interpret-btn"
          onClick={requestReading}
          disabled={!allFilled || polling}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {polling ? (
            <>
              <Loader2 size={14} className="animate-spin" /> {t("celtic.interpreting")}
            </>
          ) : (
            <>
              <Sparkles size={14} /> {t("celtic.interpret_cta")}
            </>
          )}
        </button>
        {!allFilled && (
          <p className="text-xs italic text-[#7A7061]" data-testid="celtic-incomplete-note">
            {t("celtic.fill_all_hint", { filled: picks.filter(Boolean).length })}
          </p>
        )}
        {allFilled && !polling && !reading && (
          <button
            type="button"
            data-testid="celtic-reset-btn"
            onClick={reset}
            className="btn-ghost inline-flex items-center gap-2"
          >
            <RotateCcw size={12} /> {t("celtic.reset")}
          </button>
        )}
      </div>

      {/* ---- Report ---- */}
      {reading && (
        <div ref={reportRef} data-testid="celtic-report-wrapper" className="pt-8 border-t border-[#C1B49A]">
          <p className="label-small text-[#8B3A3A] mb-3">{t("celtic.report_eyebrow")}</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#1A1918] mb-6">
            {t("celtic.report_title")}
          </h2>
          {reading.status === "pending" && (
            <div
              data-testid="celtic-report-pending"
              className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-8 flex items-center gap-4"
            >
              <Loader2 size={20} className="animate-spin text-[#8B3A3A]" />
              <div>
                <p className="font-display text-lg text-[#1A1918]">{t("celtic.pending_title")}</p>
                <p className="text-sm text-[#4A4238]">{t("celtic.pending_body")}</p>
              </div>
            </div>
          )}
          {reading.status === "error" && (
            <div
              data-testid="celtic-report-error"
              className="border border-[#8B3A3A] bg-[#F4EEDF] p-6 text-[#8B3A3A]"
            >
              {reading.error || t("celtic.error_generic")}
            </div>
          )}
          {reading.status === "done" && reading.report_markdown && (
            <article
              data-testid="celtic-report-content"
              className="max-w-none border border-[#C1B49A] bg-[#F4EEDF] p-6 md:p-10 text-[#1A1918] leading-relaxed
                [&_p]:my-4
                [&_p]:leading-relaxed
                [&_hr]:my-8
                [&_hr]:border-0
                [&_hr]:border-t
                [&_hr]:border-[#C1B49A]
                [&_h1]:font-display
                [&_h1]:text-3xl
                [&_h1]:mt-2
                [&_h1]:mb-6
                [&_h2]:font-display
                [&_h2]:text-2xl
                [&_h2]:mt-8
                [&_h2]:mb-3
                [&_h2]:text-[#8B3A3A]
                [&_h3]:font-display
                [&_h3]:text-xl
                [&_h3]:mt-6
                [&_h3]:mb-2
                [&_ul]:list-disc
                [&_ul]:pl-6
                [&_ul]:my-4
                [&_ol]:list-decimal
                [&_ol]:pl-6
                [&_ol]:my-4
                [&_li]:my-1
                [&_em]:italic
                [&_strong]:font-semibold
                [&_blockquote]:border-l-2
                [&_blockquote]:border-[#8B3A3A]
                [&_blockquote]:pl-4
                [&_blockquote]:italic
                [&_blockquote]:my-4"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{reading.report_markdown}</ReactMarkdown>
              <div className="flex justify-end pt-6 mt-6 border-t border-[#C1B49A]">
                <button
                  type="button"
                  data-testid="celtic-new-reading-btn"
                  onClick={reset}
                  className="btn-ghost inline-flex items-center gap-2"
                >
                  <RotateCcw size={12} /> {t("celtic.new_reading")}
                </button>
              </div>
            </article>
          )}
        </div>
      )}
    </section>
  );
}

function PositionPicker({ index, label, positionNumber, value, allCards, usedSlugs, loading, disabled, onChange }) {
  const availableCards = useMemo(
    () => allCards.filter((c) => c.slug === value || !usedSlugs.has(c.slug)),
    [allCards, usedSlugs, value]
  );

  return (
    <div
      data-testid={`celtic-position-${positionNumber}`}
      className={`grid grid-cols-12 gap-4 px-4 md:px-6 py-4 items-center ${
        index > 0 ? "border-t border-[#C1B49A]" : ""
      } ${value ? "bg-[#F4EEDF]" : "bg-[#EAE0CA]/30"}`}
    >
      <div className="col-span-2 md:col-span-1 font-mono text-sm tracking-[0.2em] text-[#8B3A3A] font-semibold">
        {String(positionNumber).padStart(2, "0")}
      </div>
      <div className="col-span-10 md:col-span-5">
        <p className="font-display text-base md:text-lg text-[#1A1918] leading-tight">{label}</p>
      </div>
      <div className="col-span-12 md:col-span-6">
        <select
          data-testid={`celtic-position-select-${positionNumber}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading}
          className="bg-[#F4EEDF] border border-[#1A1918] px-3 py-2 text-sm rounded-none focus:outline-none focus:border-[#8B3A3A] w-full disabled:opacity-60"
        >
          <option value="">— kies kaart —</option>
          {availableCards.map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.number}. {c.name_nl} ({c.name_en})
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
